import React from "react";
import { NavLink } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { NAV_ITEMS, SETTINGS_ITEM } from "@/components/layout/navConfig";
import { EstablishmentSwitcher } from "@/components/layout/EstablishmentSwitcher";

function NavRow({ item, onNavigate }) {
  const { t } = useT();
  const Icon = item.icon;
  if (item.locked) {
    return (
      <NavLink
        to={`/upgrade?feature=${item.feature}`}
        onClick={onNavigate}
        data-testid={`${item.testid}-locked`}
        className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-800/40 hover:text-slate-300"
      >
        <span className="flex items-center gap-3">
          <Icon className="shrink-0 opacity-60" style={{ width: 18, height: 18 }} />
          <span>{t(item.key)}</span>
        </span>
        <Lock className="shrink-0 opacity-70" style={{ width: 14, height: 14 }} />
      </NavLink>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onNavigate}
      data-testid={item.testid}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          isActive ? "bg-slate-800 text-white" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"
        }`
      }
    >
      <Icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} />
      <span>{t(item.key)}</span>
    </NavLink>
  );
}

export default function Sidebar({ onNavigate }) {
  const { user, can, hasFeature } = useAuth();
  const showLocks = can("org.manage"); // OWNER: keep premium modules visible with a lock for upsell.
  const visible = NAV_ITEMS
    .filter((i) => can(i.permission))
    .map((i) => ({ ...i, locked: !!i.feature && !hasFeature(i.feature) }))
    .filter((i) => !i.locked || showLocks);

  return (
    <div className="flex h-full flex-col bg-[#0D1B2A]">
      {/* Brand */}
      <div className="flex h-16 items-center gap-2.5 border-b border-white/10 px-5">
        <img src="/actipilote-symbol.png" alt="ActiPilote" className="h-8 w-8 lg:h-9 lg:w-9 object-contain" />
        <span className="text-base font-bold tracking-tight" data-testid="sidebar-brand">
          <span className="text-white">Acti</span><span className="text-[#2563EB]">Pilote</span>
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" data-testid="sidebar-nav">
        {visible.map((item) => <NavRow key={item.to} item={item} onNavigate={onNavigate} />)}
        <div className="my-3 h-px bg-slate-800" />
        {can(SETTINGS_ITEM.permission) && <NavRow item={SETTINGS_ITEM} onNavigate={onNavigate} />}
      </nav>

      {/* Establishment switcher + user */}
      <div className="border-t border-white/10 p-3">
        <EstablishmentSwitcher />
        <div className="mt-2 flex items-center gap-3 px-2 py-1.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-700 text-xs font-medium text-slate-200">
            {(user?.name || "?").charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-slate-200">{user?.name}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
